// src/pages/PrivacyPolicy.js
import React from 'react';

function PrivacyPolicy() {
  return (
    <div style={{ padding: '20px', fontFamily: 'sans-serif', lineHeight: '1.6' }}>
      <h1>TT International Privacy Policy</h1>

      <h2>1. Introduction</h2>
      <p>
        At TT International, we are committed to protecting your privacy and ensuring that your
        personal data is handled in a secure and transparent manner. This Privacy Policy explains
        how we collect, use, and share your personal information when you use our platform and
        services. We comply with the General Data Protection Regulation (GDPR) and other relevant
        laws.
      </p>

      <h2>2. Information We Collect</h2>
      <p>
        We collect the following types of personal information from users and
        <em>Collaborator</em>-accounts:
      </p>
      <ul style={{ listStyle: 'disc', paddingLeft: '40px' }}>
        <li>
          <strong>Personal Details:</strong> Full name, email address, phone number, and residential
          address.
        </li>
        <li>
          <strong>Payment Information:</strong> Payment details for subscription and in-app
          purchases are handled securely through third-party payment processors.
        </li>
        <li>
          <strong>Collaborator-Account Information:</strong> Personal details of
          <em>Collaborator</em>-accounts (family and friends), as provided by the main user.
        </li>
        <li>
          <strong>Usage Data:</strong> Information about how you use our platform (e.g., IP address,
          browser type, login information, etc.).
        </li>
      </ul>

      <h2>3. How We Use Your Data</h2>
      <p>
        We use the personal information we collect for the following purposes:
      </p>
      <ul style={{ listStyle: 'disc', paddingLeft: '40px' }}>
        <li>
          <strong>Service Provision:</strong> To provide ticket advisory services, manage ticket
          resales, and process rebates.
        </li>
        <li>
          <strong>Account Management:</strong> To manage user and <em>Collaborator</em>-account
          details, payments, and communications.
        </li>
        <li>
          <strong>Improving Our Services:</strong> To enhance the platform by analysing user
          interactions and feedback.
        </li>
        <li>
          <strong>Legal Compliance:</strong> To comply with applicable legal requirements, including
          data protection laws.
        </li>
      </ul>

      <h2>4. Data Sharing</h2>
      <p>
        We may share your personal information with:
      </p>
      <ul style={{ listStyle: 'disc', paddingLeft: '40px' }}>
        <li>
          <strong>Third-Party Service Providers:</strong> To process payments or handle email
          notifications.
        </li>
        <li>
          <strong>Event Organizers:</strong> For ticket purchasing, resales, and event-related
          matters.
        </li>
        <li>
          <strong><em>Collaborator</em>-Account Holders:</strong> Personal information may be shared
          between the main user and <em>Collaborator</em>-accounts for ticketing purposes.
        </li>
        <li>
          <strong>Legal Authorities:</strong> When required to comply with legal or regulatory
          obligations.
        </li>
      </ul>

      <h2>5. Data Retention</h2>
      <p>
        We retain personal information only for as long as necessary to fulfil the purposes
        outlined in this policy, unless a longer retention period is required or permitted by law.
      </p>

      <h2>6. Your Rights Under GDPR</h2>
      <p>
        You have several rights regarding your personal data:
      </p>
      <ul style={{ listStyle: 'disc', paddingLeft: '40px' }}>
        <li>
          <strong>Right to Access:</strong> You may request access to the personal data we hold
          about you.
        </li>
        <li>
          <strong>Right to Correction:</strong> You can request corrections to any inaccurate
          information.
        </li>
        <li>
          <strong>Right to Erasure:</strong> You can request the deletion of your personal data,
          subject to legal obligations.
        </li>
        <li>
          <strong>Right to Data Portability:</strong> You can request that we provide your data in a
          structured, machine-readable format.
        </li>
        <li>
          <strong>Right to Object:</strong> You can object to the processing of your personal data
          in certain circumstances.
        </li>
      </ul>
      <p>
        To exercise these rights, please contact{' '}
        <a href="mailto:management@ttinternationalltd.co.uk">management@ttinternationalltd.co.uk</a>.
      </p>

      <h2>7. Data Security</h2>
      <p>
        We implement strict security measures to protect your personal data from unauthorized
        access, disclosure, or misuse. We use encryption and secure databases to store sensitive
        information.
      </p>

      <h2>8. Changes to the Privacy Policy</h2>
      <p>
        We may update this Privacy Policy from time to time to reflect changes in our services or
        legal obligations. Any changes will be communicated to users, and continued use of the
        platform constitutes acceptance of the updated policy.
      </p>

      <h2>9. Contact Us</h2>
      <p>
        If you have any questions about this Privacy Policy or how we handle your personal data,
        please contact us at{' '}
        <a href="mailto:management@ttinternationalltd.co.uk">management@ttinternationalltd.co.uk</a>
        .
      </p>

      <h2>10. Apple-Specific Disclosures</h2>

      <h3>10.1 In-App Subscriptions</h3>
      <p>
        We offer an auto-renewing subscription through Apple’s in-app purchase system (“IAP”).
        Apple processes all subscription transactions, including free trial activations and
        subscription renewals.
      </p>
      <p>
        <strong>Free Trials (Case-by-Case):</strong> We may provide free trials at our discretion.
        Once a free trial ends, your subscription will automatically convert to a paid subscription
        unless you cancel through your Apple account settings before the trial period expires.
        <br />
        <strong>Subscription Management:</strong> You can manage or cancel your subscription at any
        time in your device’s Account Settings (App Store → Apple ID → Subscriptions).
        <br />
        <strong>Refunds:</strong> Requests for refunds must generally go through Apple. However, you
        may also contact us at{' '}
        <a href="mailto:management@ttinternationalltd.co.uk">management@ttinternationalltd.co.uk</a>{' '}
        to request a refund, and we will guide you through the appropriate process.
      </p>

      <h3>10.2 “Request to Delete” Account Functionality</h3>
      <p>
        We utilize Apple’s “Request to Delete” functionality. When you submit an account deletion
        request via your iOS device, we will remove or anonymize your data from our systems within
        [30 days or your desired timeframe], unless otherwise required by law to retain it longer.
      </p>
      <p>
        Once your account is deleted, your in-app subscriptions will end at the conclusion of the
        current billing period, and you will lose access to any account-related features.
      </p>

      <h3>10.3 Limited Use of Apple Services</h3>
      <p>
        We do <strong>not</strong> offer “Sign in with Apple,” nor do we use Apple Pay, HealthKit,
        or other Apple frameworks besides in-app purchases.
      </p>
      <p>
        We do <strong>not</strong> collect or store additional personal data from Apple beyond what
        is necessary to facilitate subscription payments.
      </p>

      <h3>10.4 No Use of Device-Based Features</h3>
      <p>
        Our app does <strong>not</strong> request access to Location Services, Contacts, Photos,
        Camera, Microphone, or other features that would require explicit permission per Apple’s
        guidelines.
      </p>
      <p>
        We do <strong>not</strong> implement Apple’s App Tracking Transparency (ATT) framework for
        advertising or analytics, as we do not engage in cross-app tracking.
      </p>

      <h3>10.5 Push Notifications (Firebase)</h3>
      <p>
        We use Firebase to manage and deliver push notifications. We only collect limited data such
        as device type and the count of push notifications sent, strictly for performance monitoring
        and troubleshooting.
      </p>
      <p>
        We do not share or sell personal usage data to any third parties.
        <br />
        If you decide to opt out of push notifications, you can do so via your device settings.
      </p>

      <h3>10.6 Age Restriction &amp; Identity Verification</h3>
      <p>
        Our subscription and app membership are intended for individuals 18 years of age or older.
      </p>
      <p>
        We perform identity verification (including checking personal identification documents)
        during the on-boarding process to ensure compliance with age requirements.
      </p>

      <h3>10.7 Data Hosting &amp; Transfer</h3>
      <p>
        All our servers are located in the European Union (EU).
        <br />
        No personal data from Apple’s systems is transferred to our servers or stored outside of the
        EU.
        <br />
        We do not currently serve users in the United States and therefore do not process data
        subject to U.S. privacy laws (e.g., CCPA).
      </p>

      <h3>10.8 Policy Updates &amp; Notifications</h3>
      <p>
        We may occasionally update this Privacy Policy to reflect changes in our services or
        compliance requirements.
        <br />
        We will notify users of significant changes via email, in-app messages, or push
        notifications. You can always review the latest version of our Privacy Policy through the
        link provided in the App Store listing or within the app.
      </p>
    </div>
  );
}

export default PrivacyPolicy;
